import { Button, styled, useTheme } from '@mui/material';
import { Multiselect } from '../../../components/Multiselect/Multiselect';
import { useCallback, useMemo, useEffect } from 'react';
import { SelectItem, User } from '../../../types';
import { Table } from '@tanstack/react-table';
import { ClosedTimesheetApprovalTableRow } from '../hooks/useAllOPLogsMapped';
import { Typography } from '../../../components/Typography/Typography';
import { useAtom } from 'jotai';
import {
  tsApprovalsSelectedOPsFilterState,
  tsApprovalsStatusFilter,
} from '../../../state/UIAccountantState';
import { StatusFilter } from '../../../components/StatusFilter/StatusFilter';

interface OpenTimesheetsFilterSectionProps {
  table: Table<ClosedTimesheetApprovalTableRow>;
  operatingPartners: User[];
}

export const ClosedTimesheetsFilterSection = ({
  table,
  operatingPartners,
}: OpenTimesheetsFilterSectionProps) => {
  const { colors } = useTheme();
  const [selectedOPs, setSelectedOPs] = useAtom(tsApprovalsSelectedOPsFilterState);
  const [statusFilter, setStatusFilter] = useAtom(tsApprovalsStatusFilter);

  const sortedOPList = useMemo(
    () => {
      if (!operatingPartners) return [];
      const allOPs = operatingPartners
        .sort()
        .map(user => ({
          ...user,
          monthlyTimesheets: (user.monthlyTimesheets ?? []).filter(
            ts => ts.timesheetLogs && ts.timesheetLogs.length > 0
          ),
        }))
        .filter(user => user.monthlyTimesheets.length > 0)
        .map((user) => ({ id: user.id, value: user.name }));
      return allOPs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, operatingPartners]
  );

  const validSelectedOPs = useMemo(() => {
    const availableOPIds = new Set(sortedOPList.map(op => op.id));
    return selectedOPs.filter(op => availableOPIds.has(Number(op.id)));
  }, [selectedOPs, sortedOPList]);

  const onChangeOPFilter = useCallback(
    (OPs: SelectItem[] | undefined) => {
      if (OPs) {
        setSelectedOPs(OPs);
        table.getColumn('timesheetName')?.setFilterValue(OPs);
      }
    },
    [table, setSelectedOPs]
  );

  useEffect(() => {
    table.getColumn('timesheetName')?.setFilterValue(validSelectedOPs);
  }, [validSelectedOPs, table]);

  const onFiltersReset = useCallback(() => {
    setSelectedOPs([]);
  }, [setSelectedOPs]);

  return (
    <Container>
      <Multiselect
        style={{
          width: '200px',
          marginRight: '14px',
        }}
        fieldPlaceholder={'Select submitter'}
        options={sortedOPList as SelectItem[]}
        onChange={(_, OPs) => onChangeOPFilter(OPs)}
        value={validSelectedOPs}
      />
      <StatusFilter value={statusFilter} onChange={(value) => value && setStatusFilter(value)} />
      <Button
        onClick={onFiltersReset}
        variant='text'
        style={{ height: '28px', marginTop: '2px' }}
        disabled={!validSelectedOPs.length}
        sx={{
          '.MuiButton-startIcon': { marginRight: '2px' },
          '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
        }}
      >
        <Typography variant='subtitle1' color={colors.textAccent.default}>
          Reset Filters
        </Typography>
      </Button>
    </Container>
  );
};

const Container = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 24px;
`;
